import { useState, useEffect } from 'react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Dropzone } from '@/components/ui/Dropzone';
import { useApp } from '@/store/app';
import { formatBytes, loadImage, readFileAsDataURL } from '@/lib/utils';

export function ImageMetadata() {
  const { pendingFile, setPendingFile } = useApp();
  const [file, setFile] = useState<File | null>(null);
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [colors, setColors] = useState<string[]>([]);

  useEffect(() => {
    if (pendingFile && pendingFile.type.startsWith('image/')) {
      handleFile([pendingFile]);
      setPendingFile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleFile(files: File[]) {
    const f = files[0];
    if (!f) return;
    setFile(f);
    const url = await readFileAsDataURL(f);
    const im = await loadImage(url);
    setImg(im);
    setColors(extractColors(im, 6));
  }

  return (
    <div>
      <ToolHeader
        toolId="image-metadata"
        title="Image inspector"
        description="Quick metadata, dimensions, dominant color extraction, and a color-aware histogram."
      />

      {!img ? (
        <Dropzone accept="image/*" onFiles={handleFile} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="!p-0 overflow-hidden">
            <div className="bg-bg-inset p-6 flex items-center justify-center min-h-[300px]">
              <img src={img.src} alt="" className="max-w-full max-h-[420px] rounded shadow-card" />
            </div>
          </Card>
          <div className="space-y-3">
            <Card>
              <div className="text-xs font-medium text-fg-muted mb-3">Metadata</div>
              <div className="space-y-2 text-sm">
                <Row k="Filename" v={file?.name ?? '—'} />
                <Row k="Type" v={file?.type ?? '—'} />
                <Row k="Size" v={file ? formatBytes(file.size) : '—'} />
                <Row k="Dimensions" v={`${img.width} × ${img.height}`} />
                <Row k="Aspect" v={`${(img.width / img.height).toFixed(2)} : 1`} />
                <Row k="Megapixels" v={`${((img.width * img.height) / 1e6).toFixed(2)} MP`} />
              </div>
            </Card>
            <Card>
              <div className="text-xs font-medium text-fg-muted mb-3">Dominant colors</div>
              <div className="flex flex-wrap gap-2">
                {colors.map((c, i) => (
                  <div
                    key={i}
                    className="flex flex-col items-center gap-1.5"
                    title={c}
                  >
                    <div
                      className="size-12 rounded-lg border border-border shadow-sm"
                      style={{ background: c }}
                    />
                    <code className="text-2xs font-mono text-fg-muted">{c.toUpperCase()}</code>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-fg-subtle">{k}</span>
      <span className="text-sm font-mono">{v}</span>
    </div>
  );
}

function extractColors(img: HTMLImageElement, count: number): string[] {
  const c = document.createElement('canvas');
  const size = 120;
  c.width = size;
  c.height = size;
  const ctx = c.getContext('2d');
  if (!ctx) return [];
  ctx.drawImage(img, 0, 0, size, size);
  const data = ctx.getImageData(0, 0, size, size).data;
  const buckets = new Map<string, number>();
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] < 200) continue;
    const r = data[i] >> 4;
    const g = data[i + 1] >> 4;
    const b = data[i + 2] >> 4;
    const key = `${r},${g},${b}`;
    buckets.set(key, (buckets.get(key) ?? 0) + 1);
  }
  return [...buckets.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, count)
    .map(([key]) => {
      const [r, g, b] = key.split(',').map((x) => parseInt(x) << 4 | 8);
      return '#' + [r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('');
    });
}

export default ImageMetadata;
